'use strict';

/**
 *   Enhanced form validation
 *   Turns off browser validation bubbles and shows custom inline errors under
 *   each field instead. Additionally it adds an error summary at the top.
 *   Only validates on submit attempt, and then checks each time on focus change.
 */

import { fetch as fetchPolyfill } from 'whatwg-fetch';

const SELECTOR = '.js-form';
const CONTROL_SELECTOR = 'input, select, textarea';
const IGNORED_CONTROL_TYPES = ['hidden', 'submit', 'button', 'reset'];

export function init() {
    document.querySelectorAll(SELECTOR).forEach(initFormBlock);
}

function initFormBlock(section) {
    const form = section.querySelector('form');

    if (!form) {
        return;
    }

    const submitButton = section.querySelector('.js-form-submit');
    const errorSummary = section.querySelector('.js-form-errors');
    const errorSummaryList = section.querySelector('.js-form-errors-list');
    const confirmation = section.querySelector('.js-form-confirmation');
    const submitError = section.querySelector('.js-form-submit-error');

    const fields = collectFields(form);

    if (!fields.length) {
        return;
    }

    form.setAttribute('novalidate', 'novalidate');

    let hasAttemptedSubmit = false;
    let isSending = false;

    fields.forEach(field => {
        field.controls.forEach(control => {
            const eventName = isChoiceControl(control) ? 'change' : 'blur';

            control.addEventListener(eventName, () => {
                if (!hasAttemptedSubmit) {
                    return;
                }

                const message = validateField(field);

                if (message) {
                    showFieldError(field, message);
                } else {
                    clearFieldError(field);
                }

                refreshErrorSummary();
            });
        });
    });

    form.addEventListener('submit', event => {
        event.preventDefault();

        if (isSending) {
            return;
        }

        hasAttemptedSubmit = true;
        hide(submitError);

        const invalidFields = [];

        fields.forEach(field => {
            const message = validateField(field);

            if (message) {
                showFieldError(field, message);
                invalidFields.push(field);
            } else {
                clearFieldError(field);
            }
        });

        if (invalidFields.length) {
            showErrorSummary(invalidFields, true);
            return;
        }

        hideErrorSummary();
        submitForm();
    });

    function submitForm() {
        const action = form.getAttribute('action');

        if (!action) {
            showConfirmation();
            return;
        }

        if (section.dataset.confirmationType !== 'message') {
            form.submit();
            return;
        }

        startSending();

        fetchPolyfill(action, {
            method: 'POST',
            body: new FormData(form),
            credentials: 'same-origin',
        })
            .then(response => {
                if (!response.ok) {
                    throw new Error(`Form submission failed with status ${response.status}`);
                }

                showConfirmation();
            })
            .catch(() => {
                stopSending();
                show(submitError);

                if (submitError) {
                    submitError.textContent = submitError.dataset.message || '';
                }
            });
    }

    function startSending() {
        isSending = true;

        if (!submitButton) {
            return;
        }

        submitButton.disabled = true;
        submitButton.classList.add('btn--loading');
        submitButton.dataset.defaultLabel = submitButton.textContent;

        if (submitButton.dataset.sendingLabel) {
            submitButton.textContent = submitButton.dataset.sendingLabel;
        }
    }

    function stopSending() {
        isSending = false;

        if (!submitButton) {
            return;
        }

        submitButton.disabled = false;
        submitButton.classList.remove('btn--loading');

        if (submitButton.dataset.defaultLabel) {
            submitButton.textContent = submitButton.dataset.defaultLabel;
        }
    }

    function showConfirmation() {
        isSending = false;

        if (!confirmation) {
            return;
        }

        form.hidden = true;
        show(confirmation);

        confirmation.focus();
        scrollIntoView(confirmation);
    }

    function showErrorSummary(invalidFields, shouldFocus) {
        if (!errorSummary || !errorSummaryList) {
            return;
        }

        errorSummaryList.innerHTML = '';

        invalidFields.forEach(field => {
            const item = document.createElement('li');
            const link = document.createElement('a');

            link.href = `#${field.controls[0].id}`;
            link.textContent = field.message;

            link.addEventListener('click', event => {
                event.preventDefault();
                field.controls[0].focus();
                scrollIntoView(field.group);
            });

            item.appendChild(link);
            errorSummaryList.appendChild(item);
        });

        show(errorSummary);

        if (shouldFocus) {
            errorSummary.focus();
            scrollIntoView(errorSummary);
        }
    }

    function refreshErrorSummary() {
        if (!errorSummary || errorSummary.hidden) {
            return;
        }

        const stillInvalid = fields.filter(field => field.message);

        if (!stillInvalid.length) {
            hideErrorSummary();
            return;
        }

        showErrorSummary(stillInvalid, false);
    }

    function hideErrorSummary() {
        if (!errorSummary || !errorSummaryList) {
            return;
        }

        hide(errorSummary);
        errorSummaryList.innerHTML = '';
    }
}

function collectFields(form) {
    return Array.from(form.querySelectorAll('.form-group'))
        .map(group => {
            const controls = Array.from(group.querySelectorAll(CONTROL_SELECTOR))
                .filter(control => IGNORED_CONTROL_TYPES.indexOf(control.type) === -1);

            if (!controls.length) {
                return null;
            }

            return {
                group,
                controls,
                type: group.dataset.fieldType || '',
                label: group.dataset.fieldLabel || '',
                required: group.dataset.required === 'true',
                errorElement: group.querySelector('.js-form-error'),
                target: group.querySelector('fieldset') || controls[0],
                message: null,
            };
        })
        .filter(Boolean);
}

function validateField(field) {
    field.message = buildMessage(field);

    return field.message;
}

function buildMessage(field) {
    const label = field.label || 'This field';

    if (isChoiceGroup(field)) {
        const isChecked = field.controls.some(control => control.checked);

        return field.required && !isChecked ? `${label} is required` : null;
    }

    const control = field.controls[0];

    if (control.type === 'checkbox') {
        return field.required && !control.checked ? `${label} is required` : null;
    }

    if (control.validity.badInput) {
        return control.type === 'number' ? `${label} must be a number` : `${label} is not valid`;
    }

    const value = (control.value || '').trim();

    if (field.required && value === '') {
        return `${label} is required`;
    }

    if (value === '') {
        return null;
    }

    if (control.value !== value) {
        control.value = value;
    }

    if (control.validity.valid) {
        return null;
    }

    if (control.validity.typeMismatch && control.type === 'email') {
        return `${label} must be a valid email address`;
    }

    if (control.validity.rangeUnderflow) {
        return `${label} must be ${control.min} or higher`;
    }

    if (control.validity.rangeOverflow) {
        return `${label} must be ${control.max} or lower`;
    }

    return control.validationMessage || `${label} is not valid`;
}

function showFieldError(field, message) {
    if (field.errorElement) {
        field.errorElement.textContent = message;
        show(field.errorElement);
    }

    field.group.classList.add('form-group--error');
    field.target.setAttribute('aria-invalid', 'true');
}

function clearFieldError(field) {
    if (field.errorElement) {
        field.errorElement.textContent = '';
        hide(field.errorElement);
    }

    field.group.classList.remove('form-group--error');
    field.target.removeAttribute('aria-invalid');
}

function isChoiceGroup(field) {
    return Boolean(field.group.querySelector('fieldset'));
}

function isChoiceControl(control) {
    return control.type === 'checkbox' || control.type === 'radio' || control.tagName === 'SELECT';
}

function scrollIntoView(element) {
    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

function show(element) {
    if (element) {
        element.hidden = false;
    }
}

function hide(element) {
    if (element) {
        element.hidden = true;
    }
}
